import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import moment from 'moment'
// import numeral from 'numeraljs'
import shortid from 'shortid'

import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from 'react-sortable-hoc'

import Input, { Label } from './input'
import Button from './button'
import HeaderPanel, { Panel } from './header-panel'
import styles from '../component-styles/manage-reports-tabs.module.scss'
import ToolTip from './tooltip'
import { availableToShareOptions, messages } from '../core/variables'
import {
  listSavedLinkPerformanceReportsAsync,
  saveLinkPerformanceReportGQL,
  updateLinkPerformanceUserPrefsGQL,
  removeSavedLinkPerformanceReportGQL,
  updateSavedLinkPerformanceReportGQL,
} from '../store/actions'

import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import ConfirmationButtons from './confirmation-buttons'
import Row, { LabelSlot } from './row'
import { GraphQLState } from '../store/graphql-reducer'
import { getItemByKeyValue } from '../helpers'
import DeleteButtonWithConfirmation from './delete-button-with-confirmation'
import ButtonTabs from './button-tabs'

export const showAccountPermissionLevel = false

interface SortableProps {
  children?: React.ReactElement
  itemIndex: number
  item: any
}

const SortableItem = SortableElement(
  ({ children, itemIndex, item }: SortableProps) => {
    return (
      <RenderRow item={item} itemIndex={itemIndex}>
        {children}
      </RenderRow>
    )
  },
)

interface RenderRowProps {
  children?: React.ReactElement
  item: any
  itemIndex: number
}
export function RenderRow({
  children,
  item,
  itemIndex = 0,
}: RenderRowProps): React.ReactElement {
  return (
    <Row align="space-between" className={styles.row} odd={!(itemIndex % 2)}>
      <div className={styles.tableCell}>
        <span className={styles.tableCellContent}>
          <span className={styles.tableCellContentPadded}>
            {item.reportName}
          </span>
        </span>
      </div>
      <div className={styles.tableCell}>
        <span className={styles.tableCellContent}>
          <span className={styles.tableCellContentPadded}>
            {availableToShareOptions[item.availableTo].long}
            {(showAccountPermissionLevel ||
              (!showAccountPermissionLevel && item.availableTo === 'user')) && (
              <b
                className={classNames(styles.availableToIcon, {
                  [styles[item.availableTo]]: true,
                })}
              />
            )}
          </span>
        </span>
      </div>
      <div className={classNames(styles.tableCell, styles.tableCellButtons)}>
        {children}
      </div>
    </Row>
  )
}
const SortableWrapper = SortableContainer(
  ({ savedReports, userPreferredOrder, onEdit }): React.ReactElement | null => {
    const dispatch = useDispatch()

    if (savedReports.length === 0) {
      return null
    }

    return (
      <div className={styles.tableBody}>
        {userPreferredOrder.map((orderKey, index) => {
          const found = getItemByKeyValue(
            savedReports,
            'savedReportID',
            orderKey,
          )
          if (found !== -1) {
            return (
              <SortableItem
                // disabled={isMobile}
                index={index}
                key={shortid.generate()}
                itemIndex={index}
                item={found}
                // setupStatus={setupStatus}
              >
                <>
                  <Button
                    type="text"
                    className={styles.editButton}
                    onClick={() => {
                      onEdit(orderKey)
                    }}
                  >
                    Edit
                  </Button>
                  <DeleteButtonWithConfirmation
                    odd={!(index % 2)}
                    className={styles.deleteButtons}
                    onClick={(): void => {
                      dispatch(
                        removeSavedLinkPerformanceReportGQL({
                          savedReportID: orderKey,
                        }),
                      )
                    }}
                  >
                    <p>Are you sure to delete this report?</p>
                  </DeleteButtonWithConfirmation>
                </>
              </SortableItem>
            )
          }
          return <span key={shortid.generate()} />
        })}
      </div>
    )
  },
)

interface ManageReportsModalProps {
  toggleActive: (val: boolean) => void
  onCreateNewReport: () => void
  onSortEnd: (any) => void
  onSortOver: (any) => void
  savedReports: any[]
  userPreferredOrder: string[]
  onEdit: (id: string) => void
  onCancel?: () => void
}

export function ManageReportsModal({
  toggleActive,
  onCreateNewReport,
  savedReports,
  userPreferredOrder,
  onSortEnd,
  onSortOver,
  onEdit,
  onCancel,
}: ManageReportsModalProps): React.ReactElement {
  const showReportsTable = useMemo(() => {
    return savedReports && savedReports.length > 0
  }, [savedReports])

  return (
    <Modal
      active
      toggleActive={() => {
        toggleActive(false)
        if (onCancel) {
          onCancel()
        }
      }}
      type="white"
    >
      <ModalHead>
        <Heading type={3} align="left">
          Manage reports
        </Heading>
      </ModalHead>
      <ModalBody noPadding>
        {showReportsTable && (
          <div className={styles.wrapper}>
            <div className={styles.body}>
              <div className={styles.reportsList}>
                <div className={styles.table}>
                  <div className={styles.tableHeader}>
                    <div className={styles.tableCell}>
                      <span className={styles.tableCellContent}>
                        Report name
                      </span>
                    </div>
                    <div className={styles.tableCell}>
                      <span className={styles.tableCellContent}>
                        Who can see this
                      </span>
                    </div>
                    <div className={styles.tableCell}>
                      <span className={styles.tableCellContent}>Actions</span>
                    </div>
                  </div>
                  <SortableWrapper
                    onEdit={onEdit}
                    onSortEnd={onSortEnd}
                    onSortOver={onSortOver}
                    savedReports={savedReports}
                    userPreferredOrder={userPreferredOrder}
                    helperClass={styles.itemDragged}
                    lockAxis="y"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <ConfirmationButtons
          type="simple"
          className={styles.confirmationButtons}
          onYes={(): void => {
            onCreateNewReport()
          }}
          onNo={(): void => {
            toggleActive(false)
            if (onCancel) {
              onCancel()
            }
          }}
          yesLabel="Create a new report"
          noLabel="Go back"
        />
      </ModalFooter>
    </Modal>
  )
}

interface SaveEditReportModalProps {
  toggleActive: (val: boolean) => void
  onSuccess: (val: any) => void
  onCancel?: () => void
  item?: any
}

export function SaveEditReportModal({
  toggleActive,
  onSuccess,
  onCancel,
  item = null,
}: SaveEditReportModalProps): React.ReactElement {
  const { reportName: _reportName, availableTo: _availableTo } = item || {
    reportName: '',
    availableTo: 'user',
  }

  const [reportName, setReportName] = useState(_reportName)
  const [availableTo, setAvailableTo] = useState(_availableTo)

  return (
    <Modal
      active
      toggleActive={() => {
        toggleActive(false)
        if (onCancel) {
          onCancel()
        }
      }}
      type="white"
      narrow
    >
      <ModalHead>
        <Heading type={3} align="left">
          {item ? 'Update existing report' : 'Create a new report'}
        </Heading>
      </ModalHead>
      <ModalBody>
        <LabelSlot topAlign className={styles.fullWidth}>
          <Label modalHeading>Report name</Label>
          <ToolTip message="Name of report to display in tab." />
        </LabelSlot>
        <Input
          name="reportName"
          id="reportName"
          type="text"
          value={reportName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
            const { value: val } = event.target
            setReportName(val)
          }}
        />
        <LabelSlot topAlign className={styles.fullWidth}>
          <Label modalHeading>Who can see this</Label>
        </LabelSlot>
        <div className={styles.radioPanel}>
          <Input
            type="radio"
            id="availableTo-1"
            name="availableTo"
            className={classNames(styles.availableToInput, {
              [styles.availableToInputCompany]:
                true && showAccountPermissionLevel,
            })}
            label={availableToShareOptions.company.long}
            checked={availableTo === 'company'}
            onClick={(): void => {
              setAvailableTo('company')
            }}
          >
            <p>{availableToShareOptions.company.desc}</p>
          </Input>
          {showAccountPermissionLevel && (
            <Input
              type="radio"
              id="availableTo-3"
              name="availableTo"
              className={classNames(styles.availableToInput, {
                [styles.availableToInputAccount]:
                  true && showAccountPermissionLevel,
              })}
              label={availableToShareOptions.account.long}
              checked={availableTo === 'account'}
              onClick={(): void => {
                setAvailableTo('account')
              }}
            >
              <p>{availableToShareOptions.account.desc}</p>
            </Input>
          )}
          <Input
            type="radio"
            id="availableTo-2"
            name="availableTo"
            className={classNames(styles.availableToInput, {
              [styles.availableToInputUser]: true,
            })}
            label={availableToShareOptions.user.long}
            checked={availableTo === 'user'}
            onClick={(): void => {
              setAvailableTo('user')
            }}
          >
            <p>{availableToShareOptions.user.desc}</p>
          </Input>
        </div>
      </ModalBody>
      <ModalFooter>
        <ConfirmationButtons
          disabled={reportName === ''}
          type="simple"
          className={styles.confirmationButtons}
          onYes={(): void => {
            onSuccess({ reportName, availableTo })
          }}
          onNo={(): void => {
            toggleActive(false)
            if (onCancel) {
              onCancel()
            }
          }}
          yesLabel={item ? 'Update' : 'Create'}
          noLabel="Go back"
        />
      </ModalFooter>
    </Modal>
  )
}

interface ManageReportsTabsSaveButtonProps {
  selectedDimensions: any
  startDate: Date
  endDate: Date
  matchType: any
  breakdownDimension: any
  successMetric: any
  currentReport: any
  onSaveReport: () => void
  disabled: boolean
  actionButton?: boolean
  currentDate: string
}

export function ManageReportsTabsSaveButton({
  selectedDimensions,
  startDate,
  endDate,
  matchType,
  breakdownDimension,
  successMetric,
  currentReport,
  onSaveReport,
  disabled,
  actionButton = false,
  currentDate,
}: ManageReportsTabsSaveButtonProps): React.ReactElement {
  const dispatch = useDispatch()

  if (disabled || !successMetric) {
    return <></>
  }

  return (
    <Button
      className={styles.noMargin}
      type={actionButton ? 'selectBox' : 'flatBlue'}
      compact={!actionButton}
      onClick={() => {
        const filterList = Object.keys(selectedDimensions).map((key) => {
          return {
            dimensionName: key,
            dimensionOptions: selectedDimensions[key],
          }
        })
        if (currentReport) {
          onSaveReport()
          const updateReportData = {
            ...currentReport,
            filterList,
            startDate:
              currentDate === 'custom'
                ? moment(startDate).format('YYYY-MM-DD')
                : currentDate,
            endDate:
              currentDate === 'custom'
                ? moment(endDate).format('YYYY-MM-DD')
                : '',
            matchType: matchType.value,
            stackDimension: breakdownDimension,
            selectedMetric: successMetric,
          }
          dispatch(updateSavedLinkPerformanceReportGQL(updateReportData))
        }
      }}
    >
      Save changes
    </Button>
  )
}

interface ManageReportsTabsProps {
  selectedDimensions: any
  startDate: Date
  endDate: Date
  matchType: any
  breakdownDimension: any
  successMetric: any
  onChangeReport: (report: SavedReport) => void
  onResetReport: () => void
  onSaveReport: () => void
  showSaveButton: boolean
  actionButtons: React.ReactNode
  currentDate: string
}

export default function ManageReportsTabs({
  selectedDimensions,
  startDate,
  endDate,
  matchType,
  breakdownDimension,
  successMetric,
  onChangeReport,
  onResetReport,
  onSaveReport,
  showSaveButton,
  actionButtons,
  currentDate,
}: ManageReportsTabsProps): React.ReactElement | null {
  const dispatch = useDispatch()

  const [currentIndex, setCurrentIndex] = useState(0)
  const [showCreateEditModal, setShowCreateEditModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [currentReport, setCurrentReport] = useState<any>(null)
  const { graphql }: { graphql: GraphQLState } = useSelector((s: any): any => s)
  const { listSavedLinkPerformanceReports, loading } = graphql

  const isLoading = loading.indexOf('listSavedLinkPerformanceReports') !== -1

  useEffect(() => {
    dispatch(listSavedLinkPerformanceReportsAsync())
  }, [])

  useEffect(() => {
    onChangeReport(currentReport)
  }, [currentReport])

  const {
    // hiddenReportList,
    savedReports,
    userPreferredOrder,
  } = listSavedLinkPerformanceReports || {
    // hiddenReportList: [],
    savedReports: [],
    userPreferredOrder: [],
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    dispatch(
      updateLinkPerformanceUserPrefsGQL({
        newOrder: userPreferredOrder,
        saveUpdate: true,
      }),
    )
  }

  const onSortOver = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const orderedArray = arrayMove(userPreferredOrder, oldIndex, newIndex)
      dispatch(
        updateLinkPerformanceUserPrefsGQL({
          newOrder: orderedArray,
          saveUpdate: false,
        }),
      )
    }
  }

  const onEdit = (savedReportID) => {
    const found = getItemByKeyValue(
      savedReports,
      'savedReportID',
      savedReportID,
    )
    if (found !== -1) {
      setCurrentReport(found)
      setShowCreateEditModal(true)
    }
  }
  const tabsLabels = useMemo(() => {
    const res: (string | React.ReactElement)[] = ['Unfiltered report']
    userPreferredOrder.forEach((orderKey) => {
      const found = getItemByKeyValue(savedReports, 'savedReportID', orderKey)
      if (found !== -1) {
        const { reportName, availableTo } = found
        if (reportName) {
          res.push(
            <em key={shortid.generate()} className={styles.tabText}>
              {reportName}{' '}
              {(showAccountPermissionLevel ||
                (!showAccountPermissionLevel && availableTo === 'user')) && (
                <b
                  className={classNames(styles.availableToIcon, {
                    [styles[availableTo]]: true,
                  })}
                />
              )}
            </em>,
          )
        }
      }
    })

    return res
  }, [userPreferredOrder])

  return (
    <>
      <HeaderPanel className={styles.headerPanel}>
        <Panel className={styles.panel}>
          <ButtonTabs
            className={styles.buttonTabs}
            showOrphansAsTab
            selected={currentIndex}
            isTopOfBox
            tabsLabels={tabsLabels}
            type="tabs"
            onChange={(index: number) => {
              if (index === currentIndex) {
                onResetReport()
              } else if (index === 0) {
                setCurrentReport(null)
              } else {
                const found = { ...savedReports[index - 1] }
                setCurrentReport(found)
              }
              setCurrentIndex(index)
            }}
          >
            {[
              ...tabsLabels.map((item) => {
                return (
                  <span
                    key={shortid.generate()}
                    className={styles.tabBoxPlaceholder}
                  >
                    {item}
                  </span>
                )
              }),
              isLoading ? (
                <div
                  key={shortid.generate()}
                  className={styles.afterTabsButtons}
                />
              ) : (
                <div
                  key={shortid.generate()}
                  className={styles.afterTabsButtons}
                >
                  <ManageReportsTabsSaveButton
                    currentDate={currentDate}
                    disabled={!showSaveButton}
                    currentReport={currentReport}
                    selectedDimensions={selectedDimensions}
                    startDate={startDate}
                    endDate={endDate}
                    matchType={matchType}
                    breakdownDimension={breakdownDimension}
                    successMetric={successMetric}
                    onSaveReport={onSaveReport}
                  />
                  {successMetric && (
                    <Button
                      className={styles.editInlineButton}
                      type="flatBlue"
                      compact
                      onClick={() => {
                        if (userPreferredOrder.length === 0) {
                          setShowCreateEditModal(true)
                        } else {
                          setShowEditModal(true)
                        }
                      }}
                    >
                      {userPreferredOrder.length === 0
                        ? 'Create new report'
                        : 'Manage reports'}
                    </Button>
                  )}
                </div>
              ),
              <div
                key={shortid.generate()}
                className={styles.afterTabsActionButtons}
              >
                {actionButtons}
              </div>,
            ]}
          </ButtonTabs>
        </Panel>
      </HeaderPanel>
      {showCreateEditModal && (
        <SaveEditReportModal
          onCancel={() => {
            setCurrentReport(null)
            setShowEditModal(true)
          }}
          item={currentReport}
          toggleActive={setShowCreateEditModal}
          onSuccess={({ reportName, availableTo }) => {
            if (!currentReport) {
              const filterList = Object.keys(selectedDimensions).map((key) => {
                return {
                  dimensionName: key,
                  dimensionOptions: selectedDimensions[key],
                }
              })

              const newReportData = {
                filterList,
                startDate:
                  currentDate === 'custom'
                    ? moment(startDate).format('YYYY-MM-DD')
                    : currentDate,
                endDate:
                  currentDate === 'custom'
                    ? moment(endDate).format('YYYY-MM-DD')
                    : '',
                matchType: matchType.value,
                availableTo,
                stackDimension: breakdownDimension,
                selectedMetric: successMetric,
                reportName,
                reportTitle: messages.defaultReportTitle,
              }
              dispatch(saveLinkPerformanceReportGQL(newReportData))
            } else {
              const updateReportData = {
                ...currentReport,
                availableTo,
                reportName,
              }
              dispatch(updateSavedLinkPerformanceReportGQL(updateReportData))
            }
            setShowCreateEditModal(false)
          }}
        />
      )}
      {showEditModal && (
        <ManageReportsModal
          onEdit={onEdit}
          onSortEnd={onSortEnd}
          onSortOver={onSortOver}
          savedReports={savedReports}
          userPreferredOrder={userPreferredOrder}
          onCreateNewReport={() => {
            setCurrentReport(null)
            setShowEditModal(false)
            setShowCreateEditModal(true)
          }}
          toggleActive={setShowEditModal}
        />
      )}
    </>
  )
}
